import { OfferWithImage } from '@/model/offer-with-image';
import { User } from '@/model/user';
import { UserService } from '@/service/user.service';
import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterModule } from '@angular/router';
import { KeycloakProfile } from 'keycloak-js';
import { KeycloakService } from 'keycloak-angular';
import { MatCardModule } from '@angular/material/card';
import { OfferMediaDisplayComponent } from '../offer-media-display/offer-media-display.component';
import { OfferService } from '@/service/offer.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-offer-card',
  imports: [
    CommonModule,
    RouterModule,
    MatMenuModule,
    MatIconModule,
    MatCardModule,
    OfferMediaDisplayComponent,
  ],
  templateUrl: './offer-card.component.html',
  styleUrls: ['./offer-card.component.scss'],
  standalone: true,
})
export class OfferCardComponent implements OnInit {
  @Input() offer!: OfferWithImage;
  @Input() showStatus!: boolean;
  @Input() showContextMenu!: boolean;
  @Input() fromAdvisoryBoard!: boolean;
  @Input() showActions!: boolean;
  @Output() offerDeleted = new EventEmitter<string>();

  isModalOpen = false;
  user: User | null = null;
  kcUser: KeycloakProfile | null = null;
  userIsAdmin = false;
  userIsCompany = false;
  userIsAdvisoryboard = false;

  constructor(
    private router: Router,
    private userService: UserService,
    private keycloakService: KeycloakService,
    private offerService: OfferService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.keycloakService.loadUserProfile().then((user: KeycloakProfile) => {
      this.kcUser = user;
      this.userIsAdmin = this.keycloakService.getUserRoles().includes('admin');
      this.userIsCompany = this.keycloakService
        .getUserRoles()
        .includes('company');
    });

    this.userService.getUserInfo().subscribe((user) => {
      this.user = user;
      this.userIsAdvisoryboard = this.user?.isAdvisoryboard ?? false;
    });
  }

  getStatusDetails(status: string): { text: string; colorClass: string } {
    switch (status) {
      case 'REJECTED':
        return { text: 'abgelehnt', colorClass: 'bg-red-500' };
      case 'APPROVED':
        return { text: 'bestätigt', colorClass: 'bg-sondisGreen' };
      case 'NEW':
        return { text: 'wird bearbeitet', colorClass: 'bg-gray-500' };
      case 'REVISED':
      case 'REQUEST_MODIFICATION':
        return { text: 'Änderung angefordert', colorClass: 'bg-orange-500' };
      default:
        return { text: 'wird bearbeitet', colorClass: 'bg-gray-500' };
    }
  }

  openDeleteModal(): void {
    this.isModalOpen = true;
  }

  closeDeleteModal(): void {
    this.isModalOpen = false;
  }

  confirmDelete(): void {
    this.offerService.deleteOffer(this.offer.id).subscribe({
      next: () => {
        this.offerDeleted.emit(this.offer.id);
        this.closeDeleteModal();
      },
      error: (error) => {
        console.error('Fehler beim Löschen des Angebots:', error);
      },
      complete: () => {
        console.log('Löschen des Angebots erfolgreich');
      },
    });
  }

  stripHtmlPreserveNewlines(html: string): SafeHtml {
    // Replace <br>, </p>, </div> with newlines before stripping tags
    const withNewlines = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/div>/gi, '\n');

    // Strip all HTML tags
    const strippedHtml = withNewlines.replace(/<[^>]*>/g, '');

    // Convert multiple consecutive newlines to a maximum of two
    const normalizedNewlines = strippedHtml.replace(/\n{3,}/g, '\n\n');

    // Trim whitespace and return sanitized content
    return this.sanitizer.bypassSecurityTrustHtml(normalizedNewlines.trim());
  }
}
